import { useEffect, useId, useState } from 'react'
import { Logo } from '@/components/ui/Logo'
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher'
import { useSite } from '@/app/site-context'
import { subscribeToScroll } from '@/lib/scroll-store'
import { useLockBodyScroll } from '@/hooks/use-lock-body-scroll'
import { siteConfig } from '@/lib/site-config'

interface HeaderProps {
  onNavigate: (href: string) => void
}

export function Header({ onNavigate }: HeaderProps) {
  const { dictionary, pathFor, route } = useSite()
  const [condensed, setCondensed] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuId = useId()

  useLockBodyScroll(menuOpen)

  useEffect(
    () =>
      subscribeToScroll(({ y }) => {
        setCondensed(y > 40)
      }),
    [],
  )

  useEffect(() => {
    if (!menuOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  const home = pathFor('home')
  const isHome = route.kind === 'home'
  const sectionHref = (id: string) => (isHome ? `#${id}` : `${home}#${id}`)

  const links = [
    { href: sectionHref('home'), label: dictionary.nav.home },
    { href: sectionHref('services'), label: dictionary.nav.services },
    { href: sectionHref('about'), label: dictionary.nav.about },
    { href: sectionHref('contact'), label: dictionary.nav.contact },
  ]

  const handleLink = (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    setMenuOpen(false)
    if (href.startsWith('#')) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
    event.preventDefault()
    onNavigate(href)
  }

  return (
    <header className={`header ${condensed ? 'is-condensed' : ''}`.trim()}>
      <a
        className="header__brand"
        href={home}
        onClick={(event) => {
          handleLink(event, home)
        }}
      >
        <Logo size={32} />
        <span className="header__wordmark">
          DALMOS<em>SYSTEMS</em>
        </span>
      </a>

      <nav className="header__nav" aria-label={siteConfig.name}>
        {links.map((link) => (
          <a
            key={link.href}
            href={link.href}
            className="header__link"
            onClick={(event) => {
              handleLink(event, link.href)
            }}
          >
            <span>{link.label}</span>
          </a>
        ))}
      </nav>

      <div className="header__actions">
        <LanguageSwitcher onNavigate={onNavigate} />
        <a
          className="header__cta"
          href={sectionHref('contact')}
          onClick={(event) => {
            handleLink(event, sectionHref('contact'))
          }}
        >
          {dictionary.nav.cta}
        </a>
        <button
          type="button"
          className="header__burger"
          aria-expanded={menuOpen}
          aria-controls={menuId}
          aria-label={menuOpen ? dictionary.nav.closeMenu : dictionary.nav.openMenu}
          onClick={() => {
            setMenuOpen((value) => !value)
          }}
        >
          <span className="header__burger-line" />
          <span className="header__burger-line" />
        </button>
      </div>

      <div id={menuId} className={`drawer ${menuOpen ? 'is-open' : ''}`.trim()} hidden={!menuOpen}>
        <nav className="drawer__nav" aria-label={dictionary.nav.openMenu}>
          {links.map((link, index) => (
            <a
              key={link.href}
              href={link.href}
              className="drawer__link"
              style={{ '--drawer-delay': `${60 + index * 60}ms` }}
              onClick={(event) => {
                handleLink(event, link.href)
              }}
            >
              <span className="drawer__index">{String(index + 1).padStart(2, '0')}</span>
              {link.label}
            </a>
          ))}
        </nav>
        <LanguageSwitcher variant="stack" onNavigate={onNavigate} />
        <a
          className="drawer__cta"
          href={sectionHref('contact')}
          onClick={(event) => {
            handleLink(event, sectionHref('contact'))
          }}
        >
          {dictionary.nav.cta}
        </a>
      </div>
    </header>
  )
}
